import { createSelector } from '@ngrx/store';

import { getModelTypeDisplayName } from '@live/live.utils';

import { adapter } from '../reducers/live-custom-insights.reducer';
import * as fromLiveEdge from '../reducers';
import { selectLiveEdgeState } from './live-edge-base.selectors';

import ICustomInsight = Microsoft.VideoIndexer.Live.Contracts.ICustomInsight;

export const featureKey = 'liveCustomInsights';
const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors();

export const getCustomInsightsState = createSelector(selectLiveEdgeState, (state: fromLiveEdge.IState) => state.customInsights);

export const selectAllCustomInsights = createSelector(getCustomInsightsState, selectAll);
export const selectCustomInsightEntities = createSelector(getCustomInsightsState, selectEntities);
export const selectCustomInsightIds = createSelector(getCustomInsightsState, selectIds);
export const selectCustomInsightTotal = createSelector(getCustomInsightsState, selectTotal);

export const getCustomInsights = createSelector(selectAllCustomInsights, customInsights => {
  // Map the insights to include proper fields
  const mappedInsights = customInsights.map(customInsight => ({
    ...customInsight,
    id: customInsight.insightId || customInsight.id,
    insightType: `${customInsight.insightName}-${customInsight.modelType}`,
    modelTypeDisplayName: getModelTypeDisplayName(customInsight.modelType),
    description: customInsight.description || ''
  }));

  return Object.values(mappedInsights).sort((a, b) => new Date(b.lastUpdateTime).getTime() - new Date(a.lastUpdateTime).getTime());
});

export const getCustomInsightById = (id: string) => createSelector(selectEntities, entities => entities[id]);
export const isCustomInsightsLoading = createSelector(getCustomInsightsState, state => state.isLoading);
export const isCustomInsightsLoaded = createSelector(getCustomInsightsState, state => state.isLoaded);
export const isCustomInsightsCreating = createSelector(getCustomInsightsState, state => state.isCreating);
export const isCustomInsightsDeleting = createSelector(getCustomInsightsState, state => state.isDeleting);
export const isCustomInsightsUploading = createSelector(getCustomInsightsState, state => state.isImageUploading);
export const getLastCreatedInsightId = createSelector(getCustomInsightsState, state => state.lastCreatedInsightId);
export const getCustomInsightsError = createSelector(getCustomInsightsState, state => state.error);
export const getTemporaryImageFile = createSelector(getCustomInsightsState, state => state.temporaryImageFile);

export const getCustomInsightImageData = createSelector(getCustomInsightsState, state => state.imageData || {});

export const isCustomInsightsImageLoading = createSelector(getCustomInsightsState, state => state.isImageLoading);

// Determine if an image belongs to positive or negative prompt
export const selectImageType = (modelId: string, imageId: string) =>
  createSelector(getCustomInsightById(modelId), (model): Microsoft.VideoIndexer.Live.Contracts.ImageType | null => {
    if (model?.prompt?.images?.some(img => img.id === imageId)) {
      return 'positive';
    }
    if (model?.negativePrompt?.images?.some(img => img.id === imageId)) {
      return 'negative';
    }
    return null;
  });

export const selectIsImageUploading = createSelector(getCustomInsightsState, state => state.isImageUploading);

export const selectIsImageDeleting = createSelector(getCustomInsightsState, state => state.isImageDeleting);
